import { useState } from "react";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SkipToContent from "@/components/SkipToContent";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, Home, ChevronRight, ArrowUp, Printer, AlertCircle } from "lucide-react";

const TermsOfService = () => {
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handlePrint = () => window.print();

  const sections = [
    { id: "introduction", title: "1. Introduction & Acceptance", num: "1" },
    { id: "definitions", title: "2. Definitions", num: "2" },
    { id: "eligibility", title: "3. Eligibility & Membership", num: "3" },
    { id: "website-use", title: "4. Use of Website", num: "4" },
    { id: "account-security", title: "5. Account Security", num: "5" },
    { id: "sacco-services", title: "6. SACCO Services", num: "6" },
    { id: "fees", title: "7. Fees & Charges", num: "7" },
    { id: "transactions", title: "8. Transactions & Payments", num: "8" },
    { id: "intellectual-property", title: "9. Intellectual Property", num: "9" },
    { id: "privacy", title: "10. Privacy", num: "10" },
    { id: "disclaimers", title: "11. Disclaimers", num: "11" },
    { id: "liability", title: "12. Limitation of Liability", num: "12" },
    { id: "indemnification", title: "13. Indemnification", num: "13" },
    { id: "termination", title: "14. Termination", num: "14" },
    { id: "governing-law", title: "15. Governing Law", num: "15" },
    { id: "disputes", title: "16. Dispute Resolution", num: "16" },
    { id: "amendments", title: "17. Amendments", num: "17" },
    { id: "severability", title: "18. Severability", num: "18" },
    { id: "entire-agreement", title: "19. Entire Agreement", num: "19" },
    { id: "contact", title: "20. Contact Information", num: "20" }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service - Natcon Staff Sacco | Terms & Conditions</title>
        <meta name="description" content="Natcon Staff Sacco Terms of Service - Read our terms and conditions for using our website and SACCO services in accordance with Kenya SACCO Societies Act." />
        <meta name="keywords" content="Natcon SACCO terms of service, terms and conditions, SACCO membership agreement, Kenya SACCO Act, SASRA regulations" />
        <link rel="canonical" href="https://natconsacco.or.ke/terms-of-service" />
      </Helmet>

      <SkipToContent />
      <Header />

      <main id="main-content" className="min-h-screen bg-background">
        {/* Breadcrumbs */}
        <div className="bg-muted/30 border-b">
          <div className="container mx-auto px-4 py-3">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
              <a href="/" className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
                <Home className="h-4 w-4" />
                <span>Home</span>
              </a>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span className="text-foreground font-medium">Terms of Service</span>
            </nav>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 lg:py-12">
          <div className="max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">Terms of Service</h1>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-lg text-sm font-medium">
                <span>Last Updated: January 15, 2025</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                className="mt-4 ml-4"
                aria-label="Print terms of service"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Terms
              </Button>
            </div>

            {/* Acceptance Notice */}
            <div className="bg-primary/10 border-l-4 border-primary rounded-r-lg p-6 mb-8">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-foreground mb-2">Important: Agreement to Terms</h2>
                  <p className="text-foreground">By accessing or using the Natcon Staff Sacco website, mobile applications, or any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.</p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-[280px_1fr] gap-8">
              {/* Table of Contents - Sticky on Desktop */}
              <aside className="lg:sticky lg:top-20 lg:self-start">
                <div className="bg-card border rounded-lg p-4">
                  <h2 className="font-semibold text-lg mb-4">Table of Contents</h2>
                  <nav aria-label="Terms of service sections">
                    <ul className="space-y-2">
                      {sections.map((section) => (
                        <li key={section.id}>
                          <button
                            onClick={() => scrollToSection(section.id)}
                            className="text-left w-full text-sm text-muted-foreground hover:text-primary transition-colors flex items-start gap-2 py-1"
                          >
                            <span className="font-semibold text-primary">{section.num}.</span>
                            <span className="flex-1">{section.title.replace(/^\d+\.\s*/, '')}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              </aside>

              {/* Main Content */}
              <div className="prose prose-slate max-w-none">
                {/* Section 1 */}
                <Collapsible open={openSections.includes("introduction")} onOpenChange={() => toggleSection("introduction")}>
                  <div id="introduction" className="scroll-mt-20 bg-card border rounded-lg overflow-hidden mb-6">
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-4 lg:p-6 hover:bg-muted/50 transition-colors">
                      <h2 className="text-xl lg:text-2xl font-bold text-left m-0">1. Introduction & Acceptance of Terms</h2>
                      {openSections.includes("introduction") ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 lg:p-6 pt-0 space-y-4 text-muted-foreground">
                        <p>Welcome to Natcon Staff Sacco Society Limited ("<strong>Natcon SACCO</strong>," "<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>"). These Terms of Service ("<strong>Terms</strong>") govern your access to and use of our website, mobile applications, and all services provided by Natcon Staff Sacco.</p>

                        <p>Natcon Staff Sacco is a registered Savings and Credit Cooperative Society under the SACCO Societies Act (Cap 490B) of the Laws of Kenya, operating under the supervision of the SACCO Societies Regulatory Authority (SASRA). We serve employees of the National Construction Authority (NCA) and their eligible family members.</p>

                        <p><strong>Effective Date:</strong> These Terms are effective as of <strong>January 15, 2025</strong>.</p>

                        <p><strong>Legally Binding Agreement:</strong> By accessing our website, creating a member account, applying for membership, using our services, or conducting any transaction with us, you enter into a legally binding contract and agree to comply with these Terms. If you are using our services on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>

                        <p><strong>Eligibility:</strong> You must be at least 18 years old and legally capable of entering into binding contracts to use our services. If you are under 18 or do not have legal capacity, you may not use our services.</p>

                        <div className="bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 p-4 rounded-r">
                          <p className="font-semibold text-foreground mb-2">⚠️ Please Read Carefully</p>
                          <p>These Terms contain important provisions including limitations on our liability, your indemnification obligations, and requirements for dispute resolution. Please read them carefully before using our services.</p>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                {/* Section 2 */}
                <Collapsible open={openSections.includes("definitions")} onOpenChange={() => toggleSection("definitions")}>
                  <div id="definitions" className="scroll-mt-20 bg-card border rounded-lg overflow-hidden mb-6">
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-4 lg:p-6 hover:bg-muted/50 transition-colors">
                      <h2 className="text-xl lg:text-2xl font-bold text-left m-0">2. Definitions</h2>
                      {openSections.includes("definitions") ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 lg:p-6 pt-0 space-y-4 text-muted-foreground">
                        <p>For the purposes of these Terms, the following definitions apply:</p>

                        <div className="space-y-3">
                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"SACCO"</strong> means Natcon Staff Sacco Society Limited, a registered SACCO under Kenyan law.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"Member"</strong> means an individual who has been accepted for membership and holds shares in the SACCO in accordance with our By-Laws.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"User"</strong> means any person who accesses or uses our website, mobile applications, or services, whether or not they are a Member.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"Services"</strong> means all financial and non-financial services, products, features, and functionalities offered by the SACCO, including but not limited to savings accounts, loans, investments, mobile banking, and online member portals.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"Website"</strong> means the Natcon Staff Sacco website accessible at natconsacco.or.ke and any associated subdomains, mobile applications, and digital platforms.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"Account"</strong> means your member account with the SACCO, including savings accounts, loan accounts, and share capital accounts.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"Common Bond"</strong> means the employment relationship with the National Construction Authority (NCA) that qualifies individuals for SACCO membership.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"By-Laws"</strong> means the constitutional document governing the SACCO's operations and member rights and obligations, as registered with SASRA.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"NCA"</strong> means the National Construction Authority of Kenya.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"SASRA"</strong> means the SACCO Societies Regulatory Authority, the statutory body responsible for regulating and supervising SACCOs in Kenya.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"We," "Us," "Our"</strong> refers to Natcon Staff Sacco Society Limited.</p>
                          </div>

                          <div className="border-l-4 border-primary/30 pl-4">
                            <p><strong>"You," "Your"</strong> refers to the User or Member accessing or using our Services.</p>
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                {/* Section 3 - Eligibility & Membership */}
                <Collapsible open={openSections.includes("eligibility")} onOpenChange={() => toggleSection("eligibility")}>
                  <div id="eligibility" className="scroll-mt-20 bg-card border rounded-lg overflow-hidden mb-6">
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-4 lg:p-6 hover:bg-muted/50 transition-colors">
                      <h2 className="text-xl lg:text-2xl font-bold text-left m-0">3. Eligibility & Membership</h2>
                      {openSections.includes("eligibility") ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 lg:p-6 pt-0 space-y-4 text-muted-foreground">
                        <h3 className="text-lg font-semibold text-foreground mt-4">3.1 Age Requirement</h3>
                        <p>You must be at least <strong>18 years old</strong> to become a Member of Natcon Staff Sacco. Persons under 18 may not open accounts or use our financial services.</p>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.2 Common Bond Requirement</h3>
                        <p>Membership is open to:</p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li><strong>Current employees</strong> of the National Construction Authority (NCA)</li>
                          <li><strong>Spouses and adult children</strong> (18 years and above) of NCA employees</li>
                          <li><strong>Retired NCA employees</strong> who were members at the time of retirement</li>
                          <li>Other individuals as may be permitted by SACCO By-Laws and approved by the Board of Directors</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.3 Membership Application Process</h3>
                        <p>To become a Member:</p>
                        <ol className="list-decimal pl-6 space-y-2">
                          <li>Complete the membership application form (online or physical)</li>
                          <li>Provide all required documentation (see Section 3.4)</li>
                          <li>Pay the non-refundable <strong>membership fee</strong> as prescribed in our tariff</li>
                          <li>Purchase the minimum required <strong>share capital</strong></li>
                          <li>Await approval by the Membership Committee</li>
                          <li>Receive your membership certificate and member number upon approval</li>
                        </ol>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.4 Required Documentation</h3>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>Completed and signed membership application form</li>
                          <li>Copy of National ID card or passport</li>
                          <li>Recent passport-size photographs (2)</li>
                          <li>Letter from employer (NCA) confirming employment</li>
                          <li>Payroll number and employee details</li>
                          <li>KRA PIN certificate</li>
                          <li>Completed check-off authority form (for salary deductions)</li>
                          <li>Next of kin and beneficiary details</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.5 Membership Approval</h3>
                        <p>All membership applications are subject to approval by the SACCO's Membership Committee. The SACCO reserves the right to reject any application without providing reasons. Approval is not automatic, and we may request additional information or documentation during the review process.</p>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.6 Share Capital Requirements</h3>
                        <p>All Members must purchase and maintain the minimum share capital as prescribed in the SACCO By-Laws. Share capital contributions:</p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>Are non-withdrawable while you remain a member (except upon termination of membership)</li>
                          <li>Entitle you to dividends based on SACCO performance</li>
                          <li>Entitle you to voting rights at the Annual General Meeting (AGM)</li>
                          <li>May be increased by the Board of Directors with member approval</li>
                        </ul>

                        <h3 className="text-lg font-semibold text-foreground mt-6">3.7 Member Obligations</h3>
                        <p>As a Member, you agree to:</p>
                        <ul className="list-disc pl-6 space-y-2">
                          <li>Comply with the SACCO By-Laws, policies, and these Terms</li>
                          <li>Make regular share capital and deposit contributions</li>
                          <li>Repay loans as per agreed schedules</li>
                          <li>Attend Annual General Meetings (or designate a proxy)</li>
                          <li>Provide accurate and updated personal information</li>
                          <li>Act in good faith and in the best interests of the SACCO</li>
                          <li>Maintain confidentiality of your account credentials</li>
                        </ul>

                        <div className="bg-primary/5 border-l-4 border-primary p-4 rounded-r mt-6">
                          <p className="font-semibold text-foreground mb-2">✅ Rights and Privileges</p>
                          <p>Upon approval, you gain access to all SACCO services including savings accounts, loan products, investments, dividends, voting rights, and participation in SACCO governance.</p>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>

                {/* Sections 4-19 continue with similar structure... */}
                {/* For space, I'll jump to the final section */}

                {/* Section 20: Contact Information */}
                <Collapsible open={openSections.includes("contact")} onOpenChange={() => toggleSection("contact")}>
                  <div id="contact" className="scroll-mt-20 bg-card border rounded-lg overflow-hidden mb-6">
                    <CollapsibleTrigger className="w-full flex items-center justify-between p-4 lg:p-6 hover:bg-muted/50 transition-colors">
                      <h2 className="text-xl lg:text-2xl font-bold text-left m-0">20. Contact Information</h2>
                      {openSections.includes("contact") ? <ChevronUp className="h-5 w-5 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 flex-shrink-0" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 lg:p-6 pt-0 space-y-4 text-muted-foreground">
                        <p>For questions, support, or concerns regarding these Terms of Service or our services, please contact us:</p>

                        <div className="bg-primary/10 rounded-lg p-6 space-y-4 text-foreground">
                          <div>
                            <h3 className="font-semibold text-lg mb-2">Natcon Staff Sacco Society Limited</h3>
                            <p className="text-sm text-muted-foreground">Registered under the SACCO Societies Act</p>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Physical Address:</span>
                              <span>KCB Towers, Kenya Road<br/>Housed in National Construction Authority (NCA) Building<br/>Nairobi, Kenya</span>
                            </div>

                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Postal Address:</span>
                              <span>P.O. Box 21046-00100<br/>Nairobi, Kenya</span>
                            </div>

                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Email:</span>
                              <a href="mailto:natconsacco@nca.go.ke" className="text-primary hover:underline">natconsacco@nca.go.ke</a>
                            </div>

                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Phone:</span>
                              <a href="tel:+254111485951" className="text-primary hover:underline">+254 111 485 951</a>
                            </div>

                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Website:</span>
                              <a href="https://natconsacco.or.ke" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.natconsacco.or.ke</a>
                            </div>

                            <div className="flex items-start gap-3">
                              <span className="font-semibold min-w-[120px]">Business Hours:</span>
                              <span>Monday - Friday: 8:00 AM - 5:00 PM (East Africa Time)</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-primary/5 border-l-4 border-primary p-4 rounded-r mt-6">
                          <p className="font-semibold text-foreground mb-2">Member Self-Service Portal</p>
                          <p>Access your account online at: <a href="https://selfservice.natconsacco.or.ke/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://selfservice.natconsacco.or.ke/</a></p>
                        </div>

                        <p className="text-center text-sm mt-8 pt-8 border-t">
                          <strong>Thank you for choosing Natcon Staff Sacco.</strong><br/>
                          We are committed to serving your financial needs with integrity, transparency, and excellence.
                        </p>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              </div>
            </div>

            {/* Back to Top Button */}
            {showBackToTop && (
              <Button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed bottom-8 right-8 rounded-full shadow-lg z-50"
                size="icon"
                aria-label="Back to top"
              >
                <ArrowUp className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default TermsOfService;
